

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewItineraryDayLineItemModal from 'components/NewItineraryDayLineItemModal';

const ItineraryDayLineItems = () => {
    const [lineItems, setLineItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('itinerary_day_line_items');

    useEffect(() => {
        fetchLineItems();
    }, []);

    const fetchLineItems = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/itinerary-day-line-items/');
            setLineItems(response.data);
        } catch (error) {
            console.error('Error fetching line items:', error);
            setSnackbar({ open: true, message: 'Error loading Line Items', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveLineItem = async (payload) => {
        try {
            await axiosInstance.post('/itinerary-day-line-items/', payload);
            setSnackbar({ open: true, message: 'Line Item created successfully', severity: 'success' });
            fetchLineItems();
        } catch (error) {
            console.error('Error saving Line Item:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/itinerary-day-line-items/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Title",
            accessor: "title",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || '-'}
                </MDTypography>
            )
        },
        { Header: "Day #", accessor: "day_number" },
        {
            Header: "Description",
            accessor: "description",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text">
                    {value || '-'}
                </MDTypography>
            )
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: lineItems,
    }), [lineItems]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Itinerary Days</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Itinerary Day
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : lineItems.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Itinerary Day Line Item" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewItineraryDayLineItemModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLineItem}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default ItineraryDayLineItems;
